import { shallowEqual } from '@apia/store';
import { useRef, useState } from 'react';
import { TPanelName, TPanelType } from '../types';
import { panelsStore, usePanelStoreSelector } from './panelsStore';
import { usePanelIdentity } from '../common';

export const undefinedObject = {};

/**
 * Permite escuchar el store de un panel al igual que se haría con un
 * useSelector de redux.
 *
 * @param selector Una función selectora que recibe el estado del panel y devuelve la selección deseada
 * @param comparator Opcionalmente se puede pasar un comparador para determinar si el estado debe actualizarse o no, dependiendo de la selección actual y la selección previa. Por defecto se usa shallowEqual de redux.
 * @returns La selección determinada en el selector, siempre que haya cambiado de acuerdo al comparador.
 *
 * @example
 *
  import { TRecordsPanelStore } from '..../recordsPanel';

  // Assume TRecordsPanelStore = { peopleNames: string[] }

  const firstName = usePanelSelector<TRecordsPanelStore>(
    'RecordsPanel',
    'RecordThisYear',
    (state) => { return state.peopleNames[0] }
  );
 */
export function usePanelSelector<StoreType, Selected>(
  panelType: TPanelType,
  panelName: TPanelName,
  initialState: StoreType,
  selector: (state: StoreType) => Selected,
  comparator: (a: Selected, b: Selected) => boolean = shallowEqual,
): Selected {
  const { panelId } = usePanelIdentity();
  if (!panelId) throw new Error(`Cannot find panel with name ${panelName}`);

  const storeState = panelsStore.getState().panelsSlice[panelType];
  const [selection, setSelection] = useState<Selected>(
    initialState
      ? selector((storeState?.[panelId] as StoreType) ?? initialState)
      : ({} as Selected),
  );

  const previousSelected = useRef<Selected>(
    selection ?? (undefinedObject as Selected),
  );

  usePanelStoreSelector((global) => {
    const newState = global.panelsSlice[panelType]?.[panelId] as
      | StoreType
      | undefined;
    if (newState !== undefined) {
      const newSelected = selector(newState);
      if (
        previousSelected.current === undefinedObject ||
        !comparator(previousSelected.current as Selected, newSelected)
      ) {
        previousSelected.current = newSelected;
        setSelection(newSelected);
      }
    }
  });

  return selection;
}
